import React, { useState } from "react";
import styled from "styled-components";
import { FaCheck, FaInfoCircle } from "react-icons/fa";
import { RiPsychotherapyFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { toggleSectionVisibility, updateProfileData } from "../../../../../redux/actions/profile";

// Toggle Container
const ToggleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 1rem;
  background-color: #ffffff;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  max-width: 520px;
  margin: 20px auto;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:hover {
    border-color: #dee2e6;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

// Toggle Wrapper (now matching badge height exactly)
const ToggleWrapper = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
  cursor: pointer;
  flex-shrink: 0;
`;

// Hidden Checkbox
const ToggleCheckbox = styled.input.attrs({ type: "checkbox" })`
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
`;

// Toggle Slider
const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e9ecef;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 26px;
  display: flex;
  align-items: center;
  padding-left: 3px;

  ${ToggleCheckbox}:checked + & {
    background-color: #4caf50;
    padding-left: 25px;
  }

  &:before {
    content: "";
    position: absolute;
    width: 20px;
    height: 20px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);

    ${ToggleCheckbox}:checked + & {
      transform: translateX(22px);
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    }
  }
`;

// Label Container
const LabelContainer = styled.div`
  width: 100%;
  text-align: left;
`;

// Title
const ToggleTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #212529;
  display: flex;
  align-items: center;
  justify-content: left;
  gap: 10px;
`;

// Description
const ToggleDescription = styled.p`
  margin: 0 0 16px 0;
  font-size: 13px;
  color: #6c757d;
  line-height: 1.5;
  text-align: left;
`;

// Status Badge (reference height: 26px total)
const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 26px;
  padding: 0 14px;
  background-color: ${(props) => (props.active ? "#d4edda" : "#e7f3ff")};
  color: ${(props) => (props.active ? "#155724" : "#0c5460")};
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid ${(props) => (props.active ? "#c3e6cb" : "#bee5eb")};
  line-height: 1;
`;

// Control Row
const ControlRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  width: 100%;
`;

const CreatorToggle = ({ isCreator }) => {
  const [isProfessional, setIsProfessional] = useState(isCreator);
  const dispatch = useDispatch();

  const profileId = useSelector((state)=>state?.profile?.user?.profile_id);

  const handleToggle = (e) => {
    const newValue = e.target.checked;
    setIsProfessional(newValue);
    handleSave(newValue);
  };

  const handleSave = (value) => {
    let payload = new FormData();
    payload.append("is_creator", value);

    dispatch(toggleSectionVisibility(payload, profileId));
  };

  return (
    <ToggleContainer>
      <LabelContainer>
        <ToggleTitle>
          <RiPsychotherapyFill size={28} color="#495057" />
          {isProfessional
            ? "Professional Mode Enabled"
            : "Are you a qualified professional?"}
        </ToggleTitle>

        <ToggleDescription>
          {isProfessional
            ? "Your profile is visible to clients. You can create sessions, receive bookings, and conduct sessions."
            : "Enable this if you're a licensed counselor, therapist, psychiatrist, psychologist, or other qualified mental health professional."}
        </ToggleDescription>

        <ControlRow>
         {!isCreator && <ToggleWrapper>
            <ToggleCheckbox
              id="professionalToggle"
              checked={isProfessional}
              onChange={handleToggle}
              aria-label="Toggle professional status"
              disabled={isProfessional}
            />
            <ToggleSlider />
          </ToggleWrapper>}

          <StatusBadge active={isProfessional}>
            {isProfessional ? (
              <>
                <FaCheck size={11} />
                Can conduct sessions
              </>
            ) : (
              <>
                <FaInfoCircle size={11} />
                Seeking sessions
              </>
            )}
          </StatusBadge>
        </ControlRow>
      </LabelContainer>
    </ToggleContainer>
  );
};

export default CreatorToggle;
